/**
 * EpworthScale — question type 'epworth' (Sleep, Dr. Rami 2026-10-05: app-scored; 2026-10-11: the patient
 * SEES the total with its label). Eight situations, each 0–3; total 0–24; standard bands
 * 0–10 Normal range · 11–12 Mildly high · 13–15 Moderately high · 16–24 High.
 * Saved as one English answer: "Epworth 11/24 (Mildly high) — Sitting and reading: 1; …".
 */
import { useI18n } from '../../i18n'

const BANDS = [
  { max: 10, key: 'normal', en: 'Normal range' },
  { max: 12, key: 'mild', en: 'Mildly high' },
  { max: 15, key: 'moderate', en: 'Moderately high' },
  { max: 24, key: 'high', en: 'High' },
]
export const epworthBand = (n) => BANDS.find((b) => n <= b.max)
const BAND_KEY = {
  normal: 'describe.epworthBandNormal',
  mild: 'describe.epworthBandMild',
  moderate: 'describe.epworthBandModerate',
  high: 'describe.epworthBandHigh',
}

export function parseEpworth(value, items) {
  const scores = items.map(() => null)
  if (typeof value !== 'string') return scores
  items.forEach((it, i) => {
    const esc = it.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const m = value.match(new RegExp(`${esc}: ([0-3])`))
    if (m) scores[i] = Number(m[1])
  })
  return scores
}

export function formatEpworth(scores, items) {
  const done = scores.every((s) => s !== null)
  const total = scores.reduce((a, s) => a + (s ?? 0), 0)
  const head = done ? `Epworth ${total}/24 (${epworthBand(total).en})` : `Epworth (incomplete, ${scores.filter((s) => s !== null).length} of ${items.length} answered)`
  const parts = items.map((it, i) => (scores[i] === null ? null : `${it}: ${scores[i]}`)).filter(Boolean)
  return `${head} — ${parts.join('; ')}`
}

export default function EpworthScale({ question, value, onChange, disabled, tq, to }) {
  const { t } = useI18n()
  const items = question.items || []
  const labels = question.options || []
  const scores = parseEpworth(value, items)
  const done = scores.every((s) => s !== null)
  const total = scores.reduce((a, s) => a + (s ?? 0), 0)
  const band = epworthBand(total)
  const pick = (i, v) => {
    if (disabled) return
    const next = [...scores]
    next[i] = v
    onChange(formatEpworth(next, items))
  }
  return (
    <div>
      {question.prompt && <p className="text-[13px] text-gray-600 mb-2">{tq(question.prompt)}</p>}
      {items.map((it, i) => (
        <div key={it} className="py-2.5 border-t border-gray-100 first:border-t-0">
          <p className="text-[13px] text-gray-700 mb-1.5">{tq(it)}</p>
          <div className="grid grid-cols-4 gap-1">
            {labels.map((lab, v) => (
              <button
                key={lab}
                type="button"
                disabled={disabled}
                aria-pressed={scores[i] === v}
                onClick={() => pick(i, v)}
                className={`rounded-[10px] border px-0.5 py-1.5 min-h-[40px] text-[11px] leading-tight font-semibold transition-colors ${
                  scores[i] === v ? 'bg-[#46A8F9] border-[#46A8F9] text-white' : 'bg-white border-gray-200 text-gray-600'
                }`}
              >
                {v}<br />{to(lab)}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div data-bottom-inset="a score readout, not an action: it sticks inside the question list and its pill padding is the design" className="sticky bottom-0 mt-2 rounded-xl bg-[#15354F] text-white px-3 py-2.5 flex items-center justify-between text-[12.5px]">
        <span>{t('describe.epworthYourScore')}</span>
        <span className="flex items-center gap-1.5">
          <strong className="tabular-nums">{t('describe.epworthOfTotal', { score: total })}</strong>
          {done && (
            <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${band.key === 'normal' ? 'bg-white/15' : 'bg-red-200 text-red-900'}`}>
              {t(BAND_KEY[band.key])}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}
